/* eslint-disable semi */
import { createDecorator } from 'vue-class-component';
import * as utils from './utils';
import DecoratorConfigs from './DecoratorConfigs';
import SasType from '../libs/SasType';

const debug = process.env.NODE_ENV === 'development';

const DECORATOR_NAME = 'BindQuery';

/*
{ a:"1", b:"2", c:[ "1", "2", "3" ] }
    変換後のクエリ文字列=> a=1&b=2&c[]=1&c[]=2&c[]=3
    クエリ文字列から還元したデータ=> { a:"1", b:"2", c:[ "1", "2", "3" ] }

{ a:null, b:"", c:[]}
    変換後のクエリ文字列=> a&b=&c
    クエリ文字列から還元したデータ=> { a:null, b:"", c:null }

*/
// const debug = process.env.NODE_ENV === 'development';

/**
 * そのコンポーネントがクエリパラメータと連携することを宣言します
 *
 * 下記のパラメータを指定できます。
 * label: クエリパラメータと連携するクエリ変数名を指定します。
 * schema: クエリパラメータの定義を指定します。
 *   default: デフォルト値
 *   alias: クエリパラメータ名
 *   type: 型(string|number|boolean|date|array:string|array:number|array:boolean|array:date)
 *
 * コンポーネントに下記を追加します。
 * this.$_sas.query: クエリパラメータへのアクセッサ
 * this.$_sas.convertToQuery: クエリパラメータへの変換メソッド
 *
 * typeは、dataメソッドで定義されているデフォルト値によって自動判別します。
 * デフォルト値がnullまたは空の配列の場合、typeを明示的に宣言する必要があります。
 * モデルの項目名とクエリパラメータ名が同一で、typeの明示的な宣言も不要な場合、
 * descriptorsを指定する必要はありません。
 *
 * this.$route.queryでは、クエリパラメータとモデルが正しくマッピングされない、
 * という問題が発生します。このデコレータは、双方向で正しくマッピングされるため
 * の変換インターフェースを提供します。
 */
export default function BindQuery (...args) {
  if (utils.isDecoratorCall(args)) {
    createAppDecorator()(...args);
  } else {
    return createAppDecorator(...args);
  }
}

const queryBinder = {
  /**
   * 値をクローンする
   * @param {*} value
   */
  clone: function (value) {
    if (value === null) {
      return null;
    }

    if (Array.isArray(value)) {
      const clone = [];
      value.forEach(element => {
        clone.push(this.clone(element));
      });
      return clone;
    } else {
      const type = SasType.decide(value);
      if (type === SasType.OBJECT || type === SasType.UNKNOWN) {
        console.error(`can not clone object. ${value}`);
        return null;
      } else {
        return value;
      }
    }
  },

  /**
   * 値がデフォルト値と同一か判別する
   * @param {*} relation
   * @param {*} value
   */
  isDefaultValue: function (relation, value) {
    return JSON.stringify(relation.default) === JSON.stringify(value);
  },

  /**
   * モデルとクエリの関連情報を生成する
   * @param {*} model
   * @param {*} schema
   */
  bind (model = {}, schema = null) {
    const queryRelations = [];
    const props = ((schema) || {}).props || {};

    // モデルとスキーマで定義されたプロパティ名の一覧を取得する
    const names = Array.from(new Set([
      ...(Object.keys(model)),
      ...(Object.keys(props))
    ]));
    names.forEach(name => {
      const prop = props[name] || {};
      const value = (name in model) ? model[name] : ('default' in prop) ? prop.default : null;

      // 型判別
      // schemaでの指定は、"."で型情報を付与できるので、それを除去する。
      const type = ('type' in prop) ? SasType.trimTypeClass(prop.type) : SasType.decide(value);

      // 2階層以上の配列型は不可
      if (SasType.getArrayLevel(type) > 1) {
        throw Error('Can not bind nested arrays to query.');
      }

      // date型は不可
      // object型は不可
      // 不明型は不可
      if (type.includes(SasType.DATE) || type.includes(SasType.OBJECT) || type.includes(SasType.UNKNOWN)) {
        throw Error('Can not bind date, object and unknown to query.');
      }

      // クエリとモデルの関連性定義として保存する
      queryRelations.push({
        model: name,
        query: ('alias' in prop) ? prop.alias : name,
        type: type,
        default: this.clone(value)
      });
    });

    if (debug) {
      console.debug(`${DECORATOR_NAME}:`, queryRelations);
    }

    return queryRelations;
  },

  /**
   * クエリからモデルに変換する
   * @param {*} relations
   * @param {*} query
   */
  convertQueryToModel (relations, query = {}) {
    const model = {};

    if (debug) {
      console.debug('convertQueryToModel', query);
    }

    relations.forEach(relation => {
      if (relation.query in query) {
        // クエリ値を設定する
        model[relation.model] = this.convertQueryValueToModelValue(
          relation,
          query[relation.query]
        );
      } else {
        // 未定義の場合はデフォルト値を設定する
        model[relation.model] = this.clone(relation.default);
      }
    });

    if (debug) {
      console.debug('convertQueryToModel', model);
    }

    return model;
  },

  /**
   * モデルからクエリに変換する
   * @param {*} relations
   * @param {*} model
   */
  convertModelToQuery (relations, model = {}) {
    const query = {};

    if (debug) {
      console.debug('convertModelToQuery', model);
    }

    relations
      .filter(relation => relation.model in model)
      .forEach(relation => {
        // 値がデフォルト値と同一の場合はクエリに格納しない
        let value = model[relation.model];
        if (!this.isDefaultValue(relation, value)) {
          query[relation.query] = this.convertModelValueToQueryValue(
            relation,
            value
          );
        }
      });

    if (debug) {
      console.debug('convertModelToQuery', query);
    }

    return query;
  },

  /**
   * モデル値をクエリ値に変換する
   * @param {*} relation
   * @param {*} src
   */
  convertModelValueToQueryValue (relation, src) {
    let value = src;

    // 型をString型に変換する
    return this.clone(
      this.convertToStr(
        value,
        relation.type
      ));
  },

  /**
   * クエリ値をモデル値に変換する
   * @param {*} relation
   * @param {*} src
   */
  convertQueryValueToModelValue (relation, src) {
    let value = src;

    // 配列の場合null値を空配列に補正する
    // （クエリ値の場合の特別処理）
    if (value === null) {
      if (SasType.isArray(relation.type)) {
        value = [];
      }
    }

    // 型をString型から元の型に変換する
    return this.clone(
      this.convertFromStr(
        value,
        relation.type
      ));
  },

  // --
  // 変換系関数
  // --

  /**
   * stringへの型変換処理
   * 変換元の型がarray型の場合は要素の型をstring型に変換する
   *
   * @param {*} value
   * @param {*} from
   */
  convertToStr (value, from) {
    const to = SasType.replaceItemType(from, SasType.STR);
    return this.convert(value, from, to);
  },

  /**
   * stringからの型変換処理
   * 変換先のがarray型の場合は要素の型をstring型から変換する
   *
   * @param {*} value
   * @param {*} from
   */
  convertFromStr (value, to) {
    const from = SasType.replaceItemType(to, SasType.STR);
    return this.convert(value, from, to);
  },

  /**
   * 値の型変換を行う
   * @param {*} value
   * @param {*} from
   * @param {*} to
   */
  convert (value, from, to) {
    let result = null;

    if (!SasType.isArray(from)) {
      // 値の変換
      result = this.convertValue(value, from, to);
    } else {
      // 配列の変換
      if (Array.isArray(value)) {
        const itemTypeFrom = SasType.getItemType(from);
        const itemTypeTo = SasType.getItemType(to);
        result = [];
        for (const idx in value) {
          result.push(this.convert(value[idx], itemTypeFrom, itemTypeTo));
        }
      } else {
        return null;
      }
    }
    return result;
  },

  /**
   * 型変換処理（いずれかはstringでなければならない）
   * @param {*} value
   * @param {*} from
   * @param {*} to
   */
  convertValue (value, from, to) {
    if (debug) {
      console.debug(JSON.stringify(value), from, to);
    }

    if (from !== SasType.STR && to !== SasType.STR) {
      throw new Error(`${from} can not be convert to ${to}.`);
    }

    if (from === SasType.STR) {
      return this.convertValueFromStr(value, to);
    } else {
      return this.convertValueToStr(value, from);
    }
  },

  /**
   * string型からの変換処理
   * @param {*} value
   * @param {*} to
   */
  convertValueFromStr (value, to) {
    if (value === null) {
      return null;
    }

    // 型チェック
    const type = SasType.decide(value);
    if (type !== SasType.STR) {
      throw new Error(`invalid type ${type}, expected type is string. [${value}]`);
    }

    switch (to) {
      case SasType.STR: return value;
      case SasType.NUM: return this.convertValueFromStrToNum(value);
      case SasType.BOOL: return this.convertValueFromStrToBool(value);
      default: throw new Error(`can not convert to ${to}.`);
    }
  },

  /**
   * string型への変換処理
   * @param {*} value
   * @param {*} to
   */
  convertValueToStr (value, from) {
    if (value === null) {
      return null;
    }

    // 型チェック
    const type = SasType.decide(value);
    if (type !== from) {
      throw new Error(`invalid type ${type}, expected type is ${from}. [${value}]`);
    }

    switch (from) {
      case SasType.STR: return value;
      case SasType.NUM: return this.convertValueFromNumToStr(value);
      case SasType.BOOL: return this.convertValueFromBoolToStr(value);
      default: throw new Error(`can not convert from ${from}.`);
    }
  },

  convertValueFromStrToNum (value) {
    if (value === null) {
      return null;
    }
    return isNaN(value * 1) ? null : value * 1;
  },

  convertValueFromNumToStr (value) {
    if (value === null) {
      return null;
    }
    return String(value);
  },

  convertValueFromStrToBool (value) {
    if (value === null) {
      return null;
    }
    return (value === 'true') ? true : (value === 'false') ? false : null;
  },

  convertValueFromBoolToStr (value) {
    if (value === null) {
      return null;
    }
    return value.toString();
  }
};

function createAppDecorator (decoratorOptions = {}) {
  // 設定値のマージ
  decoratorOptions = DecoratorConfigs.marge(DECORATOR_NAME, decoratorOptions);

  // 実装時の整合性チェック
  if (debug) {
    utils.validateDecoratorOptions(
      DECORATOR_NAME, {
        label: { type: 'string', mustBe: ['not empty'] },
        schema: { type: 'object' }
      },
      decoratorOptions
    );
  }

  return createDecorator((options, key) => {
    // 実装時の整合性チェック
    if (debug) {
      console.debug(`${DECORATOR_NAME}: @ ${key}`);

      if (key !== 'data') {
        throw new Error(`${DECORATOR_NAME}: can decorate only data().`);
      }
    }

    // オプション値取得
    const label = decoratorOptions.label || 'query';
    const schema = decoratorOptions.schema || null;

    // created時の処理を追加
    const created = options.created || function () { };
    options.created = utils.injectBefore(created, function () {
      // 実装時の整合性チェック
      if (debug) {
        if (!(label in this)) {
          throw new Error(
            `${DECORATOR_NAME}: ${label} must be declared in data().`
          );
        }
      }

      // クエリのgetterと変換関数を追加する
      // $_sas.query
      // $_sas.convertToQuery()
      const queryRelations = queryBinder.bind(this[label], schema);
      this.$_sas = this.$_sas || {};
      Object.defineProperty(this.$_sas, 'query', {
        configurable: true,
        enumerable: true,
        get: () => {
          return queryBinder.convertQueryToModel(
            queryRelations,
            this.$route.query
          );
        }
      });
      this.$_sas.convertToQuery = function (model, options = {}) {
        const merged = {};
        Object.assign(merged, model);
        Object.assign(merged, options);
        return queryBinder.convertModelToQuery(queryRelations, merged);
      };
    });

    // destroyed時の処理を追加
    const destroyed = options.destroyed || function () { };
    options.destroyed = utils.injectAfter(destroyed, function () {
      // デコレータ管理用変数破棄
      if (this.$_sas) {
        delete this.$_sas.query;
        delete this.$_sas.convertToQuery;
        if (Object.keys(this.$_sas).length === 0) {
          delete this.$_sas;
        }
      }
    });
  });
}



// WEBPACK FOOTER //
// ./src/core/decorators/BindQuery.js